<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
    <div class="px-4 py-3 border-b border-gray-200">
      <h2 class="heading-section">Call History</h2>
    </div>
    <div style="height: 400px;">
      <ag-grid-vue
        style="width: 100%; height: 100%"
        :theme="theme"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :row-model-type="'serverSide'"
        :grid-options="gridOptions"
        @grid-ready="onGridReady"
        @row-clicked="$emit('row-clicked', $event)"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { AgGridVue } from 'ag-grid-vue3'
import CellStatusBadge from '@/components/Outreach/Cells/CellStatusBadge.vue'
import { formatRelativeTime, formatDuration } from '@/utils/date'
import { formatLabel } from '@/utils/string'
import { standardListTheme } from '@/utils/common'

export default {
  name: 'CallHistoryCard',
  components: {
    AgGridVue,
    CellStatusBadge,
  },
  emits: ['row-clicked'],
  data() {
    return {
      gridApi: null,
      theme: standardListTheme,
      gridOptions: {
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        cacheBlockSize: 50,
        maxBlocksInCache: 2,
        suppressCellFocus: true,
      },
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: true,
      },
      columnDefs: [
        { field: 'to_phone_number', headerName: 'Phone', cellClass: 'sensitive' },
        {
          field: 'status',
          headerName: 'Status',
          valueGetter: (params) => formatLabel(String(params.data.status || '')),
          cellRenderer: CellStatusBadge,
          filter: 'agSetColumnFilter',
        },
        {
          field: 'started_at',
          headerName: 'Started',
          valueGetter: (params) => {
            return params.data.started_at ? new Date(String(params.data.started_at)) : null
          },
          valueFormatter: (params) => {
            return params.data.started_at ? formatRelativeTime(String(params.data.started_at)) : '-'
          },
          filter: 'agDateColumnFilter',
        },
        {
          field: 'duration',
          headerName: 'Duration',
          valueGetter: (params) => {
            if (!params.data.started_at || !params.data.ended_at) return 0
            const start = new Date(String(params.data.started_at))
            const end = new Date(String(params.data.ended_at))
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0
            return Math.floor((end.getTime() - start.getTime()) / 1000)
          },
          valueFormatter: (params) => {
            return formatDuration(String(params.data.started_at), String(params.data.ended_at))
          },
          filter: 'agNumberColumnFilter'
        },
        {
          field: 'configuration_name',
          headerName: 'Config',
          valueFormatter: (params) => params.value || '-'
        },
        {
          field: 'appointment',
          headerName: 'Appointment',
          valueGetter: (params) => {
            return params.data.appointment?.confirmation_number || null
          },
          valueFormatter: (params) => {
            if (!params.data.appointment) return '-'
            return params.data.appointment.confirmation_number
          },
          cellRenderer: (params) => {
            if (!params.data.appointment) return '-'
            const span = document.createElement('span')
            span.className = 'px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800'
            span.textContent = params.data.appointment.confirmation_number
            return span
          }
        },
      ],
    }
  },
  methods: {
    getRowId(params) {
      return params.data.id
    },
    async onGridReady(params) {
      this.gridApi = params.api

      const datasource = {
        getRows: async (paramsRequest) => {
          try {
            const response = await axios.get('api/outreach/calls/', {
              params: {
                limit: paramsRequest.request.endRow - paramsRequest.request.startRow,
                offset: paramsRequest.request.startRow,
                exclude_pending: true,
              }
            })

            if (response.data?.results) {
              const rowData = response.data.results
              paramsRequest.success({
                rowData: rowData,
                rowCount: rowData.length < 50 ? paramsRequest.request.startRow + rowData.length : undefined,
              })
            } else {
              paramsRequest.success({
                rowData: [],
                rowCount: 0
              })
            }
          } catch (error) {
            console.error('Error fetching calls:', error)
            paramsRequest.fail()
          }
        },
      }

      this.gridApi.setGridOption('serverSideDatasource', datasource)
    },
  },
}
</script>
